/**
 * @module app.shared
 */

import { autoinject, TaskQueue } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { fireEvent } from 'app/shared/lib/fire-events';
import { ITranslation, translate } from 'app/shared/lib/i18n';
import { printElement, lookupPrintOptions, IPrintable, PrintOptions } from 'app/shared/lib/printing';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { State } from 'app/shared/lib/state';
import { IDialog } from 'app/shared/elements/ess-dialog';
import { IReturnFocus, getFocusSelector, returnFocus } from 'app/shared/lib/focus';

export type DialogType = 'modal' | 'dialog';

export interface IDialogOptions {
    focus?: Element;
    width?: string;
    height?: string;
    title?: ITranslation | string;
    helpTopic?: string;
    popout?: IModalPopoutSettings;
    openRecord?: IModalOpenRecordSettings;
    print?: PrintOptions;
    type?: DialogType;
    confirmClose?: boolean;
    confirmType?: string;
    confirmMessage?: string;
}

export interface IModalPopoutSettings {
    route: string;
    params?: Object;
}

export interface IModalOpenRecordSettings {
    route: string;
    params?: Object;
    tooltipDesc?: ITranslation | string;
}

export interface IModalData {
    componentId: string;
    modelData: any;
    dialogOptions?: IDialogOptions;
    returnFocus?: IReturnFocus | null;
}

type OpenModalCustomEvent = CustomEvent<IModalData>;

interface IComposeViewModel {
    currentViewModel: any;
}

@autoinject()
export class EssGlobalModalCustomElement {

    ////****************** Fields and Properties ************************************////
    protected componentId: string | null = null;
    protected modelData?: any;
    protected options?: IDialogOptions | null;
    protected title?: string | null;
    protected isOpen: boolean = false;
    protected confirmClose: boolean = false;
    protected confirmType: string | null = null;
    protected confirmMessage: string | null = null;
    protected router!: Router;

    protected currentReturnFocus: IReturnFocus | null = null;
    protected modalStack: IModalData[] = [];

    ////****************** Element References ***************************************////
    protected $modal!: IDialog;
    protected $printArea!: Element;
    protected $composeViewModels: IComposeViewModel[] = [];

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly env: EnvironmentService,
        private readonly taskQueue: TaskQueue,
        private readonly i18n: I18N,
        private readonly logger: AppLogger,
        private readonly state: State,
        router?: Router) {

        this.router = router as any;
        this.logger.source = this;
    }

    protected detached(): void {
        this.reset();
    }


    ////****************** Events ***************************************************////
    protected openModal(event: OpenModalCustomEvent) {
        const { detail } = event;
        if(!!this.$modal && !!detail?.componentId) {

            if(this.isOpen && this.modalStack.length > 0) {
                while(this.state.loading) {
                    this.state.endLoad();
                }
                this.modalStack[this.modalStack.length - 1].returnFocus = getFocusSelector();
            } else {
                this.currentReturnFocus = getFocusSelector();
            }

            this.pushModalToStack(detail);
            this.loadModalData(detail);
            this.isOpen = true;
            this.$modal.open();
            this.resetModalScrollPosition();
        }
    }

    protected closeModal() {
        this.$modal.close();
    }

    protected async printModal(): Promise<void> {
        try {
            this.state.beginProcessing();
            let currentModalIndex = 0;
            if(this.modalStack.length < 1) {
                this.logger.warn('No modal is currently open to print.');
                return;
            }
            currentModalIndex = this.modalStack.length - 1;
            const printable = (this.$composeViewModels[currentModalIndex]?.currentViewModel || null) as (IPrintable | null);
            if(printable && printable.prepareForPrinting) {
                await printable.prepareForPrinting();
            }
            let title = this.title || undefined;
            if(title) {
                // HACK: to make filenames better (replace bullet with hyphen)
                // manually passing an alternate 'title' in print options may be the better solution
                title = title.replace('\u2022', '-');
            }
            const printOptions = lookupPrintOptions(this.env.config, this.options?.print || false, title);
            await printElement(this.$printArea, printOptions);
        }
        finally {
            this.state.endProcessing();
        }
    }

    protected popoutModal() {
        if(!this.options?.popout?.route) return;
        const { route, params } = this.options.popout;
        this.closeModal();
        const url = this.router.generate(route, params);
        window.open(url, "_blank");
    }


    ////****************** Functions ************************************************////
    private pushModalToStack(data: IModalData): void {
        this.modalStack.push(data);
    }

    private loadModalData(data: IModalData): void {

        if(data) {
            this.modelData = data.modelData;
            this.componentId = data.componentId;
            this.options = data.dialogOptions;

            const { title } = data.dialogOptions!;
            if(!!title) {
                this.title = typeof title === 'string' ? title : translate(this.i18n, title);
            }
            const type = data.dialogOptions?.type || 'dialog';
            this.$modal.isDismissible = type === 'dialog';

            const confirmClose = data.dialogOptions?.confirmClose || false;
            this.confirmClose = confirmClose;

            const confirmType = data.dialogOptions?.confirmType || null;
            this.confirmType = confirmType;

            const confirmMessage = data.dialogOptions?.confirmMessage || null;
            this.confirmMessage = confirmMessage;
        }
    }

    // Queue after bindings/render so the dialog body exists for the current modal content.
    private resetModalScrollPosition(): void {
        this.taskQueue.queueTask(() => {
            this.$modal.$body.scrollTop = 0;
        });
    }

    protected loadNextModal(): boolean {
        this.modalStack.pop();

        if(this.modalStack.length > 0) {
            const previousModal = this.modalStack[this.modalStack.length - 1];
            this.loadModalData(previousModal);

            if(previousModal.returnFocus) {
                const returnFocusTarget = previousModal.returnFocus;
                previousModal.returnFocus = null;

                this.taskQueue.queueTask(() => {
                    if(returnFocusTarget) returnFocus(returnFocusTarget);
                });
            }

            return false;
        } else if(this.currentReturnFocus) {
            const currentReturnFocus = this.currentReturnFocus;
            this.currentReturnFocus = null;

            this.taskQueue.queueTask(() => {
                if(currentReturnFocus) returnFocus(currentReturnFocus);
            });
        }

        return true;
    }

    private reset(): void {
        this.isOpen = false;
        this.componentId = null;
        this.modelData = null;
        this.options = null;
        this.title = null;
        this.modalStack = [];
        this.currentReturnFocus = null;
    }

    protected updateLoading(loading: boolean): void {
        if(loading) {
            this.state.beginLoad();
        } else {
            this.state.endLoad();
        }
    }

    ////****************** Service Calls ********************************************////

}

/**
 * Opens the <ess-modal> on app.html. It has a compose element that is configured via the function parameters
 * @param componentId The path to the component to load in the modal
 * @param modelData The data that is passed in to model.bind
 * @param dialogOptions Display options for the modal
 */
export function openGlobalModal(modalData: IModalData): void {
    if(!modalData || !modalData.componentId) { return; }
    const element = document.getElementById('ess-global-modal');  // in <ess-global-modal> custom element on app.html
    if(element) {
        fireEvent(element, 'open-global-modal', modalData);
    }
}

/**
 * Closes the <ess-modal> on app.html
 */
export function closeGlobalModal(): void {
    const element = document.getElementById('ess-global-modal');  // in <ess-global-modal> custom element on app.html
    if(element) {
        fireEvent(element, 'close-global-modal');
    }
}
